import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'fr' | 'es' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.services': 'Services',
    'nav.assessment': 'Assessment',
    'nav.contact': 'Contact',
    'nav.consultation': 'Free Consultation',
    'nav.openMenu': 'Open navigation menu',
    'nav.closeMenu': 'Close navigation menu',
    
    // Home Page
    'home.hero.title': 'Next-Generation AI Solutions for Digital Transformation',
    'home.hero.subtitle': 'Autonomous Intelligence • Quantum Security • Advanced Infrastructure',
    'home.hero.cta1': 'Explore AI Solutions',
    'home.hero.cta2': 'Schedule Demo',
    'hero.cta.assessment': 'Take Assessment',
    
    // About Page
    'about.title': 'About QuantumBox AI',
    'about.subtitle': 'We are pioneers in AI-driven digital transformation, dedicated to revolutionizing how businesses operate, innovate, and grow in the digital age.',
    'about.mission.title': 'Our Mission',
    'about.mission.description': 'To democratize artificial intelligence and automation technologies, making cutting-edge solutions accessible to businesses of all sizes. We empower organizations to harness the transformative power of AI to drive innovation, efficiency, and sustainable growth.',
    'about.vision.title': 'Our Vision',
    'about.vision.description': 'To be the global leader in AI-powered digital transformation, creating a future where intelligent automation enhances human potential and drives unprecedented business success across all industries.',
    'about.values.title': 'Our Core Values',
    'about.values.subtitle': 'The principles that guide everything we do',
    'about.values.innovation': 'Innovation',
    'about.values.innovation.desc': 'We push the boundaries of technology to create groundbreaking solutions that transform industries.',
    'about.values.excellence': 'Excellence',
    'about.values.excellence.desc': 'We are committed to delivering the highest quality services and exceeding client expectations.',
    'about.values.collaboration': 'Collaboration',
    'about.values.collaboration.desc': 'We work closely with our clients as partners, ensuring their success drives our innovation.',
    'about.values.integrity': 'Integrity',
    'about.values.integrity.desc': 'We maintain the highest ethical standards and transparency in all our business relationships.',
    'about.story.title': 'Our Story',
    'about.story.subtitle': 'Born from a vision to democratize AI technology',
    'about.story.p1': 'Founded in the heart of Paris, QuantumBox AI emerged from a simple yet powerful belief: that artificial intelligence and automation should be accessible to every business, regardless of size or industry. Our founders, veterans of the tech industry with decades of combined experience in AI, machine learning, and enterprise software, recognized the gap between cutting-edge AI research and practical business applications.',
    'about.story.p2': 'Today, we stand at the forefront of the AI revolution, helping businesses across healthcare, finance, government, logistics, and countless other sectors transform their operations through intelligent automation. Our team of world-class engineers, data scientists, and business strategists work tirelessly to push the boundaries of what\'s possible with AI technology.',
    'about.story.p3': 'From custom Large Language Models to advanced chatbots with RAG embeddings, from cloud infrastructure to security solutions, we\'ve built our reputation on delivering results that matter. Every project we undertake is driven by our commitment to excellence and our passion for innovation.',
    'about.cta.title': 'Ready to Transform Your Business?',
    'about.cta.subtitle': 'Join the hundreds of companies that have revolutionized their operations with QuantumBox AI.',
    'about.cta.button': 'Start Your Journey',
    
    // Services
    'services.title': 'Our Services',
    'services.subtitle': 'Comprehensive AI and technology solutions designed to transform your business, optimize operations, and drive sustainable growth in the digital era.',
    'services.digital.title': 'Digital Transformation Solutions',
    'services.digital.desc': 'Comprehensive end-to-end solutions to modernize your business processes and embrace the digital future.',
    'services.ai.title': 'AI & Automation',
    'services.ai.desc': 'Advanced artificial intelligence solutions that automate complex workflows and optimize business operations.',
    'services.workflow.title': 'Workflow Optimization',
    'services.workflow.desc': 'Streamline your operations with intelligent tools and strategies that enhance efficiency and productivity.',
    'services.document.title': 'Document Automation',
    'services.document.desc': 'Smart solutions for processing both structured and unstructured documents with AI-powered accuracy.',
    'services.model.title': 'Custom Model Training',
    'services.model.desc': 'Private Large Language Models and Small Language Models tailored to your specific business needs.',
    'services.chatbot.title': 'Advanced Chatbots',
    'services.chatbot.desc': 'Next-generation conversational AI with RAG embeddings for superior customer interaction and support.',
    'services.app.title': 'App Development',
    'services.app.desc': 'Custom web and mobile application development using cutting-edge technologies and frameworks.',
    'services.consulting.title': 'Consulting Services',
    'services.consulting.desc': 'Expert strategic guidance to navigate your digital transformation journey with confidence.',
    'services.cloud.title': 'Cloud Computing',
    'services.cloud.desc': 'Robust, scalable cloud solutions that ensure secure and efficient business operations.',
    'services.security.title': 'Security Solutions',
    'services.security.desc': 'Comprehensive cybersecurity measures to protect your digital assets and sensitive business data.',
    'services.server.title': 'Server Solutions',
    'services.server.desc': 'Latest server technology including premium Cisco solutions for optimal business infrastructure.',
    'services.research.title': 'Research & Innovation',
    'services.research.desc': 'Cutting-edge research and development in healthcare, mining, finance, and specialized industries.',
    'services.training.title': 'Training & Certifications',
    'services.training.description': 'Comprehensive digital transformation training programs from L1 to advanced levels, available online or on-site.',
    'services.impact.title': 'Real-World Impact',
    'services.impact.subtitle': 'See how our solutions are transforming industries and delivering measurable results for our clients.',
    'services.cta.title': 'Ready to Get Started?',
    'services.cta.subtitle': 'Let\'s discuss how our services can transform your business and drive unprecedented growth.',
    'services.cta.consultation': 'Schedule Consultation',
    'services.cta.learn': 'Learn More About Us',
    
    // Contact
    'contact.title': 'Get in Touch',
    'contact.subtitle': 'Ready to transform your business with AI? Let\'s discuss how our innovative solutions can drive your digital transformation and unlock new opportunities.',
    'contact.form.title': 'Send us a Message',
    'contact.form.description': 'Fill out the form below and we\'ll get back to you within 24 hours.',
    'contact.form.firstName': 'First Name',
    'contact.form.lastName': 'Last Name',
    'contact.form.email': 'Email Address',
    'contact.form.company': 'Company',
    'contact.form.phone': 'Phone Number',
    'contact.form.message': 'Message',
    'contact.form.placeholder': 'Tell us about your project, goals, and how we can help you...',
    'contact.form.submit': 'Send Message',
    'contact.form.sending': 'Sending...',
    'contact.info.title': 'Contact Information',
    'contact.info.subtitle': 'Multiple ways to reach our team',
    'contact.info.email': 'Email',
    'contact.info.email.desc': 'Send us a message anytime',
    'contact.info.location': 'Location',
    'contact.info.location.value': 'Paris, France',
    'contact.info.location.desc': 'European headquarters',
    'contact.info.response': 'Response Time',
    'contact.info.response.value': '< 24 hours',
    'contact.info.response.desc': 'We respond quickly',
    'contact.why.title': 'Why Partner With Us?',
    'contact.why.expert': 'Expert Consultation',
    'contact.why.expert.desc': 'Free initial assessment and strategic planning',
    'contact.why.rapid': 'Rapid Response',
    'contact.why.rapid.desc': 'Quick turnaround on proposals and solutions',
    'contact.why.proven': 'Proven Results',
    'contact.why.proven.desc': 'Track record of successful implementations',
    'contact.why.support': 'Ongoing Support',
    'contact.why.support.desc': 'Continuous optimization and maintenance',
    'contact.faq.title': 'Frequently Asked Questions',
    'contact.faq.subtitle': 'Quick answers to common questions about our services',
    'contact.faq.start': 'How quickly can we get started?',
    'contact.faq.start.answer': 'Most projects can begin within 1-2 weeks after initial consultation and scope definition.',
    'contact.faq.small': 'Do you work with small businesses?',
    'contact.faq.small.answer': 'Absolutely! We provide scalable solutions for businesses of all sizes, from startups to enterprises.',
    'contact.faq.industries': 'What industries do you serve?',
    'contact.faq.industries.answer': 'We work across all industries including healthcare, finance, government, logistics, and more.',
    'contact.faq.ongoing': 'Do you provide ongoing support?',
    'contact.faq.ongoing.answer': 'Yes, we offer comprehensive maintenance, monitoring, and optimization services for all our solutions.',
    
    // Home/Hero
    'hero.badge': 'Next-Gen AI Infrastructure',
    'hero.title.quantum': 'Quantum',
    'hero.title.box': 'Box',
    'hero.title.ai': 'AI',
    'hero.tagline.1': 'Autonomous Intelligence',
    'hero.tagline.2': 'Secure Infrastructure',
    'hero.tagline.3': 'Dynamic Scalability',
    'hero.description': 'Building tomorrow\'s enterprise architecture with AI agents, neural processing networks, and quantum-secure infrastructure that adapts, learns, and evolves.',
    'hero.cta.initialize': 'Initialize System',
    'hero.cta.automation': 'Free Automation Assessment',
    'hero.cta.explore': 'Explore Architecture',
    'hero.stats.neural': 'Neural Accuracy',
    'hero.stats.processing': 'Faster Processing',
    'hero.stats.security': 'Quantum Security',
    'hero.stats.operation': 'Autonomous Operation',
    
    // Tech Services Overview
    'tech.title': 'Advanced AI Architecture',
    'tech.description': 'Enterprise-grade AI infrastructure designed for the future. Our autonomous systems create intelligent, secure, and scalable solutions that evolve with your business.',
    'tech.agents.title': 'Autonomous AI Agents',
    'tech.agents.desc': 'Self-learning neural systems that operate independently, making intelligent decisions and optimizing processes in real-time.',
    'tech.security.title': 'Quantum-Secure Infrastructure',
    'tech.security.desc': 'Next-generation security architecture with quantum encryption, threat detection, and autonomous defense systems.',
    'tech.network.title': 'Neural Network Architecture',
    'tech.network.desc': 'Distributed AI processing networks that scale dynamically and adapt to workload demands.',
    'tech.data.title': 'Intelligent Data Mesh',
    'tech.data.desc': 'Self-organizing data infrastructure with AI-driven optimization and real-time analytics.',
    'tech.zerotrust.title': 'Zero-Trust Security',
    'tech.zerotrust.desc': 'Comprehensive security ecosystem with continuous verification and threat mitigation.',
    'tech.edge.title': 'Edge AI Processing',
    'tech.edge.desc': 'Distributed intelligence at the edge with low-latency decision making and processing.',
    'tech.analytics.title': 'Predictive Analytics Engine',
    'tech.analytics.desc': 'Advanced forecasting systems that anticipate trends and optimize business outcomes.',
    'tech.orchestration.title': 'Real-Time Orchestration',
    'tech.orchestration.desc': 'Dynamic workflow management with intelligent resource allocation and optimization.',
    'tech.performance.title': 'Infrastructure Performance',
    'tech.performance.uptime': 'System Uptime',
    'tech.performance.response': 'Response Time',
    'tech.performance.operations': 'Operations/Day',
    'tech.performance.automated': 'Automated',
    'tech.cta': 'Explore Full Architecture',
    
    // Training Programs
    'training.programs.title': 'Professional Training Programs',
    'training.programs.subtitle': 'Comprehensive certification programs designed for different skill levels and industries',
    'training.delivery.title': 'Flexible Training Delivery',
    'training.delivery.online': 'Online Training',
    'training.delivery.onsite': 'On-Site Training',
    'training.delivery.hybrid': 'Hybrid Programs',
    
    // Training Levels
    'training.l1.title': 'AI Fundamentals & Prompt Engineering',
    'training.l1.description': 'Master AI basics and prompt engineering techniques for maximum productivity',
    'training.l1.topic1': 'Prompt Engineering',
    'training.l1.topic2': 'AI Model Selection',
    'training.l1.topic3': 'Safety & Ethics',
    'training.l1.topic4': 'Practical Applications',
    'training.l1.audience1': 'Beginners',
    'training.l1.audience2': 'Business Users',
    'training.l1.audience3': 'Team Leaders',
    'training.l1.audience4': 'Non-Technical Roles',
    
    'training.l2.title': 'Agentic Automation & Process Intelligence',
    'training.l2.description': 'Build autonomous AI agents that understand, learn, and optimize business processes',
    'training.l2.topic1': 'AI Agent Design',
    'training.l2.topic2': 'Autonomous Decision Making',
    'training.l2.topic3': 'Process Intelligence',
    'training.l2.topic4': 'Agent Orchestration',
    'training.l2.audience1': 'Process Managers',
    'training.l2.audience2': 'IT Professionals',
    'training.l2.audience3': 'Operations Teams',
    'training.l2.audience4': 'Automation Specialists',
    
    'training.l3.title': 'AI Integration Specialist',
    'training.l3.description': 'Advanced AI implementation and custom model integration',
    'training.l3.topic1': 'Custom AI Models',
    'training.l3.topic2': 'API Integration',
    'training.l3.topic3': 'Data Pipeline',
    'training.l3.topic4': 'Model Training',
    'training.l3.audience1': 'Software Engineers',
    'training.l3.audience2': 'Data Scientists',
    'training.l3.audience3': 'AI Developers',
    'training.l3.audience4': 'Technical Leads',
    
    'training.l4.title': 'Cloud AI Architecture & Security',
    'training.l4.description': 'Enterprise AI infrastructure design with advanced security frameworks',
    'training.l4.topic1': 'AI Infrastructure',
    'training.l4.topic2': 'Cloud Security',
    'training.l4.topic3': 'AI Governance',
    'training.l4.topic4': 'Compliance & Risk',
    'training.l4.audience1': 'Solutions Architects',
    'training.l4.audience2': 'Security Engineers',
    'training.l4.audience3': 'DevOps Teams',
    'training.l4.audience4': 'IT Directors',
    
    'training.l5.title': 'AI Strategy & Executive Leadership',
    'training.l5.description': 'C-level AI strategy, governance, and organizational transformation leadership',
    'training.l5.topic1': 'AI Strategy',
    'training.l5.topic2': 'Digital Leadership',
    'training.l5.topic3': 'Change Management',
    'training.l5.topic4': 'AI Ethics & Governance',
    'training.l5.audience1': 'C-Level Executives',
    'training.l5.audience2': 'Strategic Consultants',
    'training.l5.audience3': 'Innovation Leaders',
    'training.l5.audience4': 'Board Members',
    
    // Training Delivery Methods
    'training.online.title': 'Online Training',
    'training.online.description': 'Interactive virtual sessions with real-time collaboration',
    'training.online.feature1': 'Live Instructor',
    'training.online.feature2': 'Interactive Labs',
    'training.online.feature3': 'Recording Access',
    'training.online.feature4': 'Digital Materials',
    
    'training.onsite.title': 'On-Site Training',
    'training.onsite.description': 'Customized programs delivered at your location',
    'training.onsite.feature1': 'Custom Content',
    'training.onsite.feature2': 'Team Building',
    'training.onsite.feature3': 'Equipment Setup',
    'training.onsite.feature4': 'Follow-up Support',
    
    'training.hybrid.title': 'Hybrid Programs',
    'training.hybrid.description': 'Blend of online and in-person training for optimal flexibility',
    'training.hybrid.feature1': 'Flexible Schedule',
    'training.hybrid.feature2': 'Best of Both',
    'training.hybrid.feature3': 'Cost Effective',
    'training.hybrid.feature4': 'Global Reach',
    
    // Training UI Elements
    'training.keytopics': 'Key Topics',
    'training.targetaudience': 'Target Audience',
    'training.free': 'Free',
    'training.professional': 'Professional',
    'training.duration.hours': 'hours',
    'training.free.message.title': 'Free Training Opportunity!',
    'training.free.message.description': 'Start learning for free and earn your globally recognized certificate upon completion. No cost, just value!',
    'training.free.button': 'Start Free Training Now',
    'training.professional.title': 'Professional Training',
    'training.professional.description': 'Customized training programs with expert guidance and hands-on projects.',
    'training.book.button': 'Book This Training Now',
    'training.certification.title': 'Professional Certification Benefits',
    'training.certification.description': 'Our globally recognized certifications validate your expertise and advance your career in digital transformation.',
    'training.certification.advancement': 'Career Advancement',
    'training.certification.salary': 'Average Salary Increase',
    'training.certification.placement': 'Job Placement Rate',
    
    // Footer
    'footer.company': 'Company',
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    'footer.description': 'Leading the autonomous intelligence revolution with quantum-secure AI systems, neural processing networks, and next-generation infrastructure solutions.',
    'footer.about': 'About Us',
    'footer.neural.networks': 'Neural Networks',
    'footer.quantum.security': 'Quantum Security', 
    'footer.edge.computing': 'Edge Computing',
    'footer.ai.orchestration': 'AI Orchestration',
    'footer.neural.consulting': 'Neural Consulting',
    'footer.architecture': 'Architecture',
    'footer.neural.research': 'Neural Research',
    'footer.copyright': '© 2024 QuantumBox AI. All rights reserved. Pioneering autonomous intelligence and quantum-secure infrastructure.',
    
    // Common
    'common.learnMore': 'Learn More',
    'common.getStarted': 'Get Started',
    'common.contactUs': 'Contact Us',
    'common.required': 'required',
  },
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.about': 'À Propos',
    'nav.services': 'Services',
    'nav.assessment': 'Évaluation',
    'nav.contact': 'Contact',
    'nav.consultation': 'Consultation Gratuite',
    'nav.openMenu': 'Ouvrir le menu de navigation',
    'nav.closeMenu': 'Fermer le menu de navigation',
    
    // Home Page
    'home.hero.title': 'Solutions IA de Nouvelle Génération pour la Transformation Numérique',
    'home.hero.subtitle': 'Intelligence Autonome • Sécurité Quantique • Infrastructure Avancée',
    'home.hero.cta1': 'Explorer les Solutions IA',
    'home.hero.cta2': 'Planifier une Démo',
    'hero.cta.assessment': 'Faire l\'Évaluation',
    
    // About Page
    'about.title': 'À Propos de QuantumBox AI',
    'about.subtitle': 'Nous sommes pionniers de la transformation numérique alimentée par l\'IA, dédiés à révolutionner la façon dont les entreprises opèrent, innovent et croissent à l\'ère numérique.',
    'about.mission.title': 'Notre Mission',
    'about.mission.description': 'Démocratiser les technologies d\'intelligence artificielle et d\'automatisation, rendant les solutions de pointe accessibles aux entreprises de toutes tailles. Nous donnons aux organisations les moyens d\'exploiter le pouvoir transformateur de l\'IA pour stimuler l\'innovation, l\'efficacité et la croissance durable.',
    'about.vision.title': 'Notre Vision',
    'about.vision.description': 'Être le leader mondial de la transformation numérique alimentée par l\'IA, créant un avenir où l\'automatisation intelligente améliore le potentiel humain et génère un succès commercial sans précédent dans toutes les industries.',
    'about.values.title': 'Nos Valeurs Fondamentales',
    'about.values.subtitle': 'Les principes qui guident tout ce que nous faisons',
    'about.values.innovation': 'Innovation',
    'about.values.innovation.desc': 'Nous repoussons les limites de la technologie pour créer des solutions révolutionnaires qui transforment les industries.',
    'about.values.excellence': 'Excellence',
    'about.values.excellence.desc': 'Nous nous engageons à livrer des services de la plus haute qualité et à dépasser les attentes des clients.',
    'about.values.collaboration': 'Collaboration',
    'about.values.collaboration.desc': 'Nous travaillons étroitement avec nos clients en tant que partenaires, nous assurant que leur succès alimente notre innovation.',
    'about.values.integrity': 'Intégrité',
    'about.values.integrity.desc': 'Nous maintenons les plus hauts standards éthiques et la transparence dans toutes nos relations d\'affaires.',
    'about.story.title': 'Notre Histoire',
    'about.story.subtitle': 'Née d\'une vision de démocratiser la technologie IA',
    'about.story.p1': 'Fondée au cœur de Paris, QuantumBox AI est née d\'une croyance simple mais puissante : que l\'intelligence artificielle et l\'automatisation devraient être accessibles à chaque entreprise, quelle que soit sa taille ou son secteur. Nos fondateurs, vétérans de l\'industrie tech avec des décennies d\'expérience combinée en IA, apprentissage automatique et logiciels d\'entreprise, ont reconnu l\'écart entre la recherche de pointe en IA et les applications commerciales pratiques.',
    'about.story.p2': 'Aujourd\'hui, nous sommes à l\'avant-garde de la révolution IA, aidant les entreprises dans la santé, la finance, le gouvernement, la logistique et d\'innombrables autres secteurs à transformer leurs opérations grâce à l\'automatisation intelligente. Notre équipe d\'ingénieurs de classe mondiale, de scientifiques des données et de stratèges commerciaux travaille sans relâche pour repousser les limites de ce qui est possible avec la technologie IA.',
    'about.story.p3': 'Des modèles de langage large personnalisés aux chatbots avancés avec embeddings RAG, de l\'infrastructure cloud aux solutions de sécurité, nous avons bâti notre réputation sur la livraison de résultats qui comptent. Chaque projet que nous entreprenons est guidé par notre engagement envers l\'excellence et notre passion pour l\'innovation.',
    'about.cta.title': 'Prêt à Transformer Votre Entreprise ?',
    'about.cta.subtitle': 'Rejoignez les centaines d\'entreprises qui ont révolutionné leurs opérations avec QuantumBox AI.',
    'about.cta.button': 'Commencez Votre Voyage',
    
    // Services
    'services.title': 'Nos Services',
    'services.subtitle': 'Solutions complètes d\'IA et de technologie conçues pour transformer votre entreprise, optimiser les opérations et stimuler une croissance durable à l\'ère numérique.',
    'services.digital.title': 'Solutions de Transformation Numérique',
    'services.digital.desc': 'Solutions complètes de bout en bout pour moderniser vos processus d\'affaires et embrasser l\'avenir numérique.',
    'services.ai.title': 'IA & Automatisation',
    'services.ai.desc': 'Solutions d\'intelligence artificielle avancées qui automatisent les flux de travail complexes et optimisent les opérations commerciales.',
    'services.workflow.title': 'Optimisation des Flux de Travail',
    'services.workflow.desc': 'Rationalisez vos opérations avec des outils et stratégies intelligents qui améliorent l\'efficacité et la productivité.',
    'services.document.title': 'Automatisation de Documents',
    'services.document.desc': 'Solutions intelligentes pour traiter les documents structurés et non structurés avec une précision alimentée par l\'IA.',
    'services.model.title': 'Formation de Modèles Personnalisés',
    'services.model.desc': 'Modèles de langage large privés et modèles de langage petit adaptés à vos besoins commerciaux spécifiques.',
    'services.chatbot.title': 'Chatbots Avancés',
    'services.chatbot.desc': 'IA conversationnelle de nouvelle génération avec embeddings RAG pour une interaction et un support client supérieurs.',
    'services.app.title': 'Développement d\'Applications',
    'services.app.desc': 'Développement d\'applications web et mobiles personnalisées utilisant des technologies et frameworks de pointe.',
    'services.consulting.title': 'Services de Conseil',
    'services.consulting.desc': 'Guidance stratégique experte pour naviguer votre parcours de transformation numérique avec confiance.',
    'services.cloud.title': 'Informatique en Nuage',
    'services.cloud.desc': 'Solutions cloud robustes et évolutives qui garantissent des opérations commerciales sécurisées et efficaces.',
    'services.security.title': 'Solutions de Sécurité',
    'services.security.desc': 'Mesures de cybersécurité complètes pour protéger vos actifs numériques et données commerciales sensibles.',
    'services.server.title': 'Solutions Serveur',
    'services.server.desc': 'Dernière technologie serveur incluant des solutions Cisco premium pour une infrastructure commerciale optimale.',
    'services.research.title': 'Recherche & Innovation',
    'services.research.desc': 'Recherche et développement de pointe dans la santé, l\'exploitation minière, la finance et les industries spécialisées.',
    'services.training.title': 'Formation & Certifications',
    'services.training.description': 'Programmes de formation complets en transformation numérique du niveau L1 aux niveaux avancés, disponibles en ligne ou sur site.',
    'services.impact.title': 'Impact Réel',
    'services.impact.subtitle': 'Découvrez comment nos solutions transforment les industries et offrent des résultats mesurables à nos clients.',
    'services.cta.title': 'Prêt à Commencer ?',
    'services.cta.subtitle': 'Discutons de la façon dont nos services peuvent transformer votre entreprise et stimuler une croissance sans précédent.',
    'services.cta.consultation': 'Planifier une Consultation',
    'services.cta.learn': 'En Savoir Plus Sur Nous',
    
    // Contact
    'contact.title': 'Entrer en Contact',
    'contact.subtitle': 'Prêt à transformer votre entreprise avec l\'IA ? Discutons de la façon dont nos solutions innovantes peuvent stimuler votre transformation numérique et débloquer de nouvelles opportunités.',
    'contact.form.title': 'Envoyez-nous un Message',
    'contact.form.description': 'Remplissez le formulaire ci-dessous et nous vous répondrons dans les 24 heures.',
    'contact.form.firstName': 'Prénom',
    'contact.form.lastName': 'Nom de Famille',
    'contact.form.email': 'Adresse Email',
    'contact.form.company': 'Entreprise',
    'contact.form.phone': 'Numéro de Téléphone',
    'contact.form.message': 'Message',
    'contact.form.placeholder': 'Parlez-nous de votre projet, de vos objectifs et de la façon dont nous pouvons vous aider...',
    'contact.form.submit': 'Envoyer le Message',
    'contact.form.sending': 'Envoi...',
    'contact.info.title': 'Informations de Contact',
    'contact.info.subtitle': 'Plusieurs façons de joindre notre équipe',
    'contact.info.email': 'Email',
    'contact.info.email.desc': 'Envoyez-nous un message à tout moment',
    'contact.info.location': 'Localisation',
    'contact.info.location.value': 'Paris, France',
    'contact.info.location.desc': 'Siège social européen',
    'contact.info.response': 'Temps de Réponse',
    'contact.info.response.value': '< 24 heures',
    'contact.info.response.desc': 'Nous répondons rapidement',
    'contact.why.title': 'Pourquoi Nous Choisir ?',
    'contact.why.expert': 'Consultation d\'Expert',
    'contact.why.expert.desc': 'Évaluation initiale gratuite et planification stratégique',
    'contact.why.rapid': 'Réponse Rapide',
    'contact.why.rapid.desc': 'Délai d\'exécution rapide sur les propositions et solutions',
    'contact.why.proven': 'Résultats Prouvés',
    'contact.why.proven.desc': 'Historique d\'implémentations réussies',
    'contact.why.support': 'Support Continu',
    'contact.why.support.desc': 'Optimisation et maintenance continues',
    'contact.faq.title': 'Questions Fréquemment Posées',
    'contact.faq.subtitle': 'Réponses rapides aux questions courantes sur nos services',
    'contact.faq.start': 'À quelle vitesse pouvons-nous commencer ?',
    'contact.faq.start.answer': 'La plupart des projets peuvent commencer dans 1-2 semaines après la consultation initiale et la définition de la portée.',
    'contact.faq.small': 'Travaillez-vous avec les petites entreprises ?',
    'contact.faq.small.answer': 'Absolument ! Nous fournissons des solutions évolutives pour les entreprises de toutes tailles, des startups aux grandes entreprises.',
    'contact.faq.industries': 'Quels secteurs servez-vous ?',
    'contact.faq.industries.answer': 'Nous travaillons dans tous les secteurs incluant la santé, la finance, le gouvernement, la logistique et plus encore.',
    'contact.faq.ongoing': 'Fournissez-vous un support continu ?',
    'contact.faq.ongoing.answer': 'Oui, nous offrons des services complets de maintenance, surveillance et optimisation pour toutes nos solutions.',
    
    // Home/Hero
    'hero.badge': 'Infrastructure IA Nouvelle Génération',
    'hero.title.quantum': 'Quantum',
    'hero.title.box': 'Box',
    'hero.title.ai': 'IA',
    'hero.tagline.1': 'Intelligence Autonome',
    'hero.tagline.2': 'Infrastructure Sécurisée',
    'hero.tagline.3': 'Évolutivité Dynamique',
    'hero.description': 'Construire l\'architecture d\'entreprise de demain avec des agents IA, des réseaux de traitement neuronal et une infrastructure sécurisée quantique qui s\'adapte, apprend et évolue.',
    'hero.cta.initialize': 'Initialiser le Système',
    'hero.cta.automation': 'Évaluation Gratuite d\'Automatisation',
    'hero.cta.explore': 'Explorer l\'Architecture',
    'hero.stats.neural': 'Précision Neuronale',
    'hero.stats.processing': 'Traitement Plus Rapide',
    'hero.stats.security': 'Sécurité Quantique',
    'hero.stats.operation': 'Opération Autonome',
    
    // Tech Services Overview
    'tech.title': 'Architecture IA Avancée',
    'tech.description': 'Infrastructure IA de niveau entreprise conçue pour l\'avenir. Nos systèmes autonomes créent des solutions intelligentes, sécurisées et évolutives qui évoluent avec votre entreprise.',
    'tech.agents.title': 'Agents IA Autonomes',
    'tech.agents.desc': 'Systèmes neuronaux auto-apprenants qui opèrent indépendamment, prenant des décisions intelligentes et optimisant les processus en temps réel.',
    'tech.security.title': 'Infrastructure Sécurisée Quantique',
    'tech.security.desc': 'Architecture de sécurité de nouvelle génération avec chiffrement quantique, détection de menaces et systèmes de défense autonomes.',
    'tech.network.title': 'Architecture de Réseau Neuronal',
    'tech.network.desc': 'Réseaux de traitement IA distribués qui s\'adaptent dynamiquement et s\'ajustent aux demandes de charge de travail.',
    'tech.data.title': 'Maillage de Données Intelligent',
    'tech.data.desc': 'Infrastructure de données auto-organisatrice avec optimisation alimentée par IA et analyses en temps réel.',
    'tech.zerotrust.title': 'Sécurité Zero-Trust',
    'tech.zerotrust.desc': 'Écosystème de sécurité complet avec vérification continue et atténuation des menaces.',
    'tech.edge.title': 'Traitement IA Edge',
    'tech.edge.desc': 'Intelligence distribuée à la périphérie avec prise de décision et traitement à faible latence.',
    'tech.analytics.title': 'Moteur d\'Analyse Prédictive',
    'tech.analytics.desc': 'Systèmes de prévision avancés qui anticipent les tendances et optimisent les résultats commerciaux.',
    'tech.orchestration.title': 'Orchestration en Temps Réel',
    'tech.orchestration.desc': 'Gestion de flux de travail dynamique avec allocation intelligente des ressources et optimisation.',
    'tech.performance.title': 'Performance de l\'Infrastructure',
    'tech.performance.uptime': 'Temps de Fonctionnement Système',
    'tech.performance.response': 'Temps de Réponse',
    'tech.performance.operations': 'Opérations/Jour',
    'tech.performance.automated': 'Automatisé',
    'tech.cta': 'Explorer l\'Architecture Complète',
    
    // Training Programs
    'training.programs.title': 'Programmes de Formation Professionnelle',
    'training.programs.subtitle': 'Programmes de certification complets conçus pour différents niveaux de compétence et industries',
    'training.delivery.title': 'Méthodes de Formation Flexibles',
    'training.delivery.online': 'Formation en Ligne',
    'training.delivery.onsite': 'Formation sur Site',
    'training.delivery.hybrid': 'Programmes Hybrides',
    
    // Training Levels
    'training.l1.title': 'Fondamentaux IA & Ingénierie de Prompts',
    'training.l1.description': 'Maîtrisez les bases de l\'IA et les techniques d\'ingénierie de prompts pour une productivité maximale',
    'training.l1.topic1': 'Ingénierie de Prompts',
    'training.l1.topic2': 'Sélection de Modèles IA',
    'training.l1.topic3': 'Sécurité & Éthique',
    'training.l1.topic4': 'Applications Pratiques',
    'training.l1.audience1': 'Débutants',
    'training.l1.audience2': 'Utilisateurs Métier',
    'training.l1.audience3': 'Chefs d\'Équipe',
    'training.l1.audience4': 'Rôles Non-Techniques',
    
    'training.l2.title': 'Automatisation Agentique & Intelligence des Processus',
    'training.l2.description': 'Créez des agents IA autonomes qui comprennent, apprennent et optimisent les processus métier',
    'training.l2.topic1': 'Conception d\'Agents IA',
    'training.l2.topic2': 'Prise de Décision Autonome',
    'training.l2.topic3': 'Intelligence des Processus',
    'training.l2.topic4': 'Orchestration d\'Agents',
    'training.l2.audience1': 'Gestionnaires de Processus',
    'training.l2.audience2': 'Professionnels IT',
    'training.l2.audience3': 'Équipes Opérationnelles',
    'training.l2.audience4': 'Spécialistes Automatisation',
    
    'training.l3.title': 'Spécialiste Intégration IA',
    'training.l3.description': 'Implémentation IA avancée et intégration de modèles personnalisés',
    'training.l3.topic1': 'Modèles IA Personnalisés',
    'training.l3.topic2': 'Intégration API',
    'training.l3.topic3': 'Pipeline de Données',
    'training.l3.topic4': 'Formation de Modèles',
    'training.l3.audience1': 'Ingénieurs Logiciel',
    'training.l3.audience2': 'Data Scientists',
    'training.l3.audience3': 'Développeurs IA',
    'training.l3.audience4': 'Responsables Techniques',
    
    'training.l4.title': 'Architecture IA Cloud & Sécurité',
    'training.l4.description': 'Conception d\'infrastructure IA d\'entreprise avec cadres de sécurité avancés',
    'training.l4.topic1': 'Infrastructure IA',
    'training.l4.topic2': 'Sécurité Cloud',
    'training.l4.topic3': 'Gouvernance IA',
    'training.l4.topic4': 'Conformité & Risques',
    'training.l4.audience1': 'Architectes Solutions',
    'training.l4.audience2': 'Ingénieurs Sécurité',
    'training.l4.audience3': 'Équipes DevOps',
    'training.l4.audience4': 'Directeurs IT',
    
    'training.l5.title': 'Stratégie IA & Leadership Exécutif',
    'training.l5.description': 'Stratégie IA niveau direction, gouvernance et leadership de transformation organisationnelle',
    'training.l5.topic1': 'Stratégie IA',
    'training.l5.topic2': 'Leadership Numérique',
    'training.l5.topic3': 'Gestion du Changement',
    'training.l5.topic4': 'Éthique & Gouvernance IA',
    'training.l5.audience1': 'Dirigeants C-Level',
    'training.l5.audience2': 'Consultants Stratégiques',
    'training.l5.audience3': 'Leaders Innovation',
    'training.l5.audience4': 'Membres Conseil',
    
    // Training Delivery Methods
    'training.online.title': 'Formation en Ligne',
    'training.online.description': 'Sessions virtuelles interactives avec collaboration en temps réel',
    'training.online.feature1': 'Instructeur en Direct',
    'training.online.feature2': 'Laboratoires Interactifs',
    'training.online.feature3': 'Accès Enregistrements',
    'training.online.feature4': 'Matériel Numérique',
    
    'training.onsite.title': 'Formation sur Site',
    'training.onsite.description': 'Programmes personnalisés dispensés sur votre site',
    'training.onsite.feature1': 'Contenu Personnalisé',
    'training.onsite.feature2': 'Team Building',
    'training.onsite.feature3': 'Configuration Équipement',
    'training.onsite.feature4': 'Support Suivi',
    
    'training.hybrid.title': 'Programmes Hybrides',
    'training.hybrid.description': 'Mélange de formation en ligne et en présentiel pour une flexibilité optimale',
    'training.hybrid.feature1': 'Horaire Flexible',
    'training.hybrid.feature2': 'Meilleur des Deux',
    'training.hybrid.feature3': 'Rentable',
    'training.hybrid.feature4': 'Portée Mondiale',
    
    // Training UI Elements
    'training.keytopics': 'Sujets Clés',
    'training.targetaudience': 'Public Cible',
    'training.free': 'Gratuit',
    'training.professional': 'Professionnel',
    'training.duration.hours': 'heures',
    'training.free.message.title': 'Opportunité de Formation Gratuite !',
    'training.free.message.description': 'Commencez à apprendre gratuitement et obtenez votre certificat reconnu mondialement à la fin. Aucun coût, que de la valeur !',
    'training.free.button': 'Commencer la Formation Gratuite',
    'training.professional.title': 'Formation Professionnelle',
    'training.professional.description': 'Programmes de formation personnalisés avec guidance experte et projets pratiques.',
    'training.book.button': 'Réserver Cette Formation',
    'training.certification.title': 'Avantages de la Certification Professionnelle',
    'training.certification.description': 'Nos certifications reconnues mondialement valident votre expertise et font progresser votre carrière en transformation numérique.',
    'training.certification.advancement': 'Avancement de Carrière',
    'training.certification.salary': 'Augmentation Salariale Moyenne',
    'training.certification.placement': 'Taux de Placement',
    
    // Footer
    'footer.company': 'Entreprise',
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.rights': 'Tous droits réservés.',
    'footer.description': 'Leader de la révolution de l\'intelligence autonome avec des systèmes IA sécurisés quantiques, des réseaux de traitement neuronal et des solutions d\'infrastructure de nouvelle génération.',
    'footer.about': 'À Propos de Nous',
    'footer.neural.networks': 'Réseaux Neuronaux',
    'footer.quantum.security': 'Sécurité Quantique',
    'footer.edge.computing': 'Edge Computing',
    'footer.ai.orchestration': 'Orchestration IA',
    'footer.neural.consulting': 'Conseil Neuronal',
    'footer.architecture': 'Architecture',
    'footer.neural.research': 'Recherche Neuronale',
    'footer.copyright': '© 2024 QuantumBox AI. Tous droits réservés. Pionnier de l\'intelligence autonome et de l\'infrastructure sécurisée quantique.',
    
    // Common
    'common.learnMore': 'En Savoir Plus',
    'common.getStarted': 'Commencer',
    'common.contactUs': 'Nous Contacter',
    'common.required': 'requis',
  },
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.about': 'Acerca de',
    'nav.services': 'Servicios',
    'nav.assessment': 'Evaluación',
    'nav.contact': 'Contacto',
    'nav.consultation': 'Consulta Gratuita',
    
    // Home Page
    'home.hero.title': 'Soluciones de IA de Nueva Generación para la Transformación Digital',
    'home.hero.subtitle': 'Inteligencia Autónoma • Seguridad Cuántica • Infraestructura Avanzada',
    'home.hero.cta1': 'Explorar Soluciones de IA',
    'home.hero.cta2': 'Programar Demo',
    'hero.cta.assessment': 'Realizar Evaluación',
    
    // About Page
    'about.title': 'Acerca de QuantumBox AI',
    'about.subtitle': 'Somos pioneros en la transformación digital impulsada por IA, dedicados a revolucionar cómo las empresas operan, innovan y crecen en la era digital.',
    'about.mission.title': 'Nuestra Misión',
    'about.mission.description': 'Democratizar las tecnologías de inteligencia artificial y automatización, haciendo las soluciones de vanguardia accesibles para empresas de todos los tamaños. Empoderamos a las organizaciones para aprovechar el poder transformador de la IA para impulsar la innovación, eficiencia y crecimiento sostenible.',
    'about.vision.title': 'Nuestra Visión',
    'about.vision.description': 'Ser el líder mundial en transformación digital impulsada por IA, creando un futuro donde la automatización inteligente mejore el potencial humano e impulse el éxito empresarial sin precedentes en todas las industrias.',
    'about.values.title': 'Nuestros Valores Fundamentales',
    'about.values.subtitle': 'Los principios que guían todo lo que hacemos',
    'about.values.innovation': 'Innovación',
    'about.values.innovation.desc': 'Empujamos los límites de la tecnología para crear soluciones revolucionarias que transforman industrias.',
    'about.values.excellence': 'Excelencia',
    'about.values.excellence.desc': 'Estamos comprometidos a entregar servicios de la más alta calidad y superar las expectativas del cliente.',
    'about.values.collaboration': 'Colaboración',
    'about.values.collaboration.desc': 'Trabajamos estrechamente con nuestros clientes como socios, asegurándonos de que su éxito impulse nuestra innovación.',
    'about.values.integrity': 'Integridad',
    'about.values.integrity.desc': 'Mantenemos los más altos estándares éticos y transparencia en todas nuestras relaciones comerciales.',
    'about.story.title': 'Nuestra Historia',
    'about.story.subtitle': 'Nacida de una visión para democratizar la tecnología de IA',
    'about.story.p1': 'Fundada en el corazón de París, QuantumBox AI surgió de una creencia simple pero poderosa: que la inteligencia artificial y la automatización deberían ser accesibles para cada empresa, sin importar su tamaño o industria. Nuestros fundadores, veteranos de la industria tecnológica con décadas de experiencia combinada en IA, aprendizaje automático y software empresarial, reconocieron la brecha entre la investigación de IA de vanguardia y las aplicaciones comerciales prácticas.',
    'about.story.p2': 'Hoy, estamos a la vanguardia de la revolución de la IA, ayudando a empresas en salud, finanzas, gobierno, logística e innumerables otros sectores a transformar sus operaciones a través de la automatización inteligente. Nuestro equipo de ingenieros de clase mundial, científicos de datos y estrategas comerciales trabaja incansablemente para empujar los límites de lo que es posible con la tecnología de IA.',
    'about.story.p3': 'Desde modelos de lenguaje grandes personalizados hasta chatbots avanzados con embeddings RAG, desde infraestructura en la nube hasta soluciones de seguridad, hemos construido nuestra reputación entregando resultados que importan. Cada proyecto que emprendemos está impulsado por nuestro compromiso con la excelencia y nuestra pasión por la innovación.',
    'about.cta.title': '¿Listo para Transformar tu Empresa?',
    'about.cta.subtitle': 'Únete a los cientos de empresas que han revolucionado sus operaciones con QuantumBox AI.',
    'about.cta.button': 'Comienza tu Viaje',
    
    // Services
    'services.title': 'Nuestros Servicios',
    'services.subtitle': 'Soluciones integrales de IA y tecnología diseñadas para transformar tu empresa, optimizar operaciones e impulsar el crecimiento sostenible en la era digital.',
    'services.digital.title': 'Soluciones de Transformación Digital',
    'services.digital.desc': 'Soluciones integrales de extremo a extremo para modernizar tus procesos comerciales y abrazar el futuro digital.',
    'services.ai.title': 'IA y Automatización',
    'services.ai.desc': 'Soluciones avanzadas de inteligencia artificial que automatizan flujos de trabajo complejos y optimizan las operaciones comerciales.',
    'services.workflow.title': 'Optimización de Flujos de Trabajo',
    'services.workflow.desc': 'Optimiza tus operaciones con herramientas y estrategias inteligentes que mejoran la eficiencia y productividad.',
    'services.document.title': 'Automatización de Documentos',
    'services.document.desc': 'Soluciones inteligentes para procesar documentos estructurados y no estructurados con precisión impulsada por IA.',
    'services.model.title': 'Entrenamiento de Modelos Personalizados',
    'services.model.desc': 'Modelos de lenguaje grandes privados y modelos de lenguaje pequeños adaptados a tus necesidades comerciales específicas.',
    'services.chatbot.title': 'Chatbots Avanzados',
    'services.chatbot.desc': 'IA conversacional de próxima generación con embeddings RAG para interacción y soporte al cliente superior.',
    'services.app.title': 'Desarrollo de Aplicaciones',
    'services.app.desc': 'Desarrollo de aplicaciones web y móviles personalizadas utilizando tecnologías y frameworks de vanguardia.',
    'services.consulting.title': 'Servicios de Consultoría',
    'services.consulting.desc': 'Orientación estratégica experta para navegar tu viaje de transformación digital con confianza.',
    'services.cloud.title': 'Computación en la Nube',
    'services.cloud.desc': 'Soluciones en la nube robustas y escalables que aseguran operaciones comerciales seguras y eficientes.',
    'services.security.title': 'Soluciones de Seguridad',
    'services.security.desc': 'Medidas de ciberseguridad integrales para proteger tus activos digitales y datos comerciales sensibles.',
    'services.server.title': 'Soluciones de Servidor',
    'services.server.desc': 'Última tecnología de servidor incluyendo soluciones Cisco premium para infraestructura comercial óptima.',
    'services.research.title': 'Investigación e Innovación',
    'services.research.desc': 'Investigación y desarrollo de vanguardia en salud, minería, finanzas e industrias especializadas.',
    'services.training.title': 'Formación y Certificaciones',
    'services.training.description': 'Programas integrales de formación en transformación digital desde nivel L1 hasta avanzado, disponibles en línea o presencialmente.',
    'services.impact.title': 'Impacto Real',
    'services.impact.subtitle': 'Ve cómo nuestras soluciones están transformando industrias y entregando resultados medibles para nuestros clientes.',
    'services.cta.title': '¿Listo para Comenzar?',
    'services.cta.subtitle': 'Hablemos sobre cómo nuestros servicios pueden transformar tu empresa e impulsar un crecimiento sin precedentes.',
    'services.cta.consultation': 'Programar Consulta',
    'services.cta.learn': 'Conoce Más Sobre Nosotros',
    
    // Contact
    'contact.title': 'Contáctanos',
    'contact.subtitle': '¿Listo para transformar tu empresa con IA? Hablemos sobre cómo nuestras soluciones innovadoras pueden impulsar tu transformación digital y desbloquear nuevas oportunidades.',
    'contact.form.title': 'Envíanos un Mensaje',
    'contact.form.description': 'Completa el formulario a continuación y te responderemos en 24 horas.',
    'contact.form.firstName': 'Nombre',
    'contact.form.lastName': 'Apellido',
    'contact.form.email': 'Dirección de Correo',
    'contact.form.company': 'Empresa',
    'contact.form.phone': 'Número de Teléfono',
    'contact.form.message': 'Mensaje',
    'contact.form.placeholder': 'Cuéntanos sobre tu proyecto, objetivos y cómo podemos ayudarte...',
    'contact.form.submit': 'Enviar Mensaje',
    'contact.form.sending': 'Enviando...',
    'contact.info.title': 'Información de Contacto',
    'contact.info.subtitle': 'Múltiples formas de contactar a nuestro equipo',
    'contact.info.email': 'Correo',
    'contact.info.email.desc': 'Envíanos un mensaje en cualquier momento',
    'contact.info.location': 'Ubicación',
    'contact.info.location.value': 'París, Francia',
    'contact.info.location.desc': 'Sede europea',
    'contact.info.response': 'Tiempo de Respuesta',
    'contact.info.response.value': '< 24 horas',
    'contact.info.response.desc': 'Respondemos rápidamente',
    'contact.why.title': '¿Por Qué Asociarte con Nosotros?',
    'contact.why.expert': 'Consulta Experta',
    'contact.why.expert.desc': 'Evaluación inicial gratuita y planificación estratégica',
    'contact.why.rapid': 'Respuesta Rápida',
    'contact.why.rapid.desc': 'Tiempo de respuesta rápido en propuestas y soluciones',
    'contact.why.proven': 'Resultados Comprobados',
    'contact.why.proven.desc': 'Historial de implementaciones exitosas',
    'contact.why.support': 'Soporte Continuo',
    'contact.why.support.desc': 'Optimización y mantenimiento continuo',
    'contact.faq.title': 'Preguntas Frecuentes',
    'contact.faq.subtitle': 'Respuestas rápidas a preguntas comunes sobre nuestros servicios',
    'contact.faq.start': '¿Qué tan rápido podemos comenzar?',
    'contact.faq.start.answer': 'La mayoría de los proyectos pueden comenzar en 1-2 semanas después de la consulta inicial y definición del alcance.',
    'contact.faq.small': '¿Trabajan con pequeñas empresas?',
    'contact.faq.small.answer': '¡Absolutamente! Proporcionamos soluciones escalables para empresas de todos los tamaños, desde startups hasta grandes corporaciones.',
    'contact.faq.industries': '¿Qué industrias atienden?',
    'contact.faq.industries.answer': 'Trabajamos en todas las industrias incluyendo salud, finanzas, gobierno, logística y más.',
    'contact.faq.ongoing': '¿Proporcionan soporte continuo?',
    'contact.faq.ongoing.answer': 'Sí, ofrecemos servicios integrales de mantenimiento, monitoreo y optimización para todas nuestras soluciones.',
    
    // Home/Hero
    'hero.badge': 'Infraestructura de IA Nueva Generación',
    'hero.title.quantum': 'Quantum',
    'hero.title.box': 'Box',
    'hero.title.ai': 'IA',
    'hero.tagline.1': 'Inteligencia Autónoma',
    'hero.tagline.2': 'Infraestructura Segura',
    'hero.tagline.3': 'Escalabilidad Dinámica',
    'hero.description': 'Construyendo la arquitectura empresarial del mañana con agentes de IA, redes de procesamiento neuronal e infraestructura cuántica segura que se adapta, aprende y evoluciona.',
    'hero.cta.initialize': 'Inicializar Sistema',
    'hero.cta.automation': 'Evaluación Gratuita de Automatización',
    'hero.cta.explore': 'Explorar Arquitectura',
    'hero.stats.neural': 'Precisión Neuronal',
    'hero.stats.processing': 'Procesamiento Más Rápido',
    'hero.stats.security': 'Seguridad Cuántica',
    'hero.stats.operation': 'Operación Autónoma',
    
    // Tech Services Overview
    'tech.title': 'Arquitectura de IA Avanzada',
    'tech.description': 'Infraestructura de IA de nivel empresarial diseñada para el futuro. Nuestros sistemas autónomos crean soluciones inteligentes, seguras y escalables que evolucionan con tu empresa.',
    'tech.agents.title': 'Agentes de IA Autónomos',
    'tech.agents.desc': 'Sistemas neuronales auto-aprendices que operan independientemente, tomando decisiones inteligentes y optimizando procesos en tiempo real.',
    'tech.security.title': 'Infraestructura Cuántica Segura',
    'tech.security.desc': 'Arquitectura de seguridad de próxima generación con encriptación cuántica, detección de amenazas y sistemas de defensa autónomos.',
    'tech.network.title': 'Arquitectura de Red Neuronal',
    'tech.network.desc': 'Redes de procesamiento de IA distribuidas que escalan dinámicamente y se adaptan a las demandas de carga de trabajo.',
    'tech.data.title': 'Malla de Datos Inteligente',
    'tech.data.desc': 'Infraestructura de datos auto-organizativa con optimización impulsada por IA y análisis en tiempo real.',
    'tech.zerotrust.title': 'Seguridad Zero-Trust',
    'tech.zerotrust.desc': 'Ecosistema de seguridad integral con verificación continua y mitigación de amenazas.',
    'tech.edge.title': 'Procesamiento de IA en el Borde',
    'tech.edge.desc': 'Inteligencia distribuida en el borde con toma de decisiones y procesamiento de baja latencia.',
    'tech.analytics.title': 'Motor de Análisis Predictivo',
    'tech.analytics.desc': 'Sistemas de pronóstico avanzados que anticipan tendencias y optimizan resultados comerciales.',
    'tech.orchestration.title': 'Orquestación en Tiempo Real',
    'tech.orchestration.desc': 'Gestión dinámica de flujos de trabajo con asignación inteligente de recursos y optimización.',
    'tech.performance.title': 'Rendimiento de Infraestructura',
    'tech.performance.uptime': 'Tiempo de Actividad del Sistema',
    'tech.performance.response': 'Tiempo de Respuesta',
    'tech.performance.operations': 'Operaciones/Día',
    'tech.performance.automated': 'Automatizado',
    'tech.cta': 'Explorar Arquitectura Completa',
    
    // Training Programs
    'training.programs.title': 'Programas de Formación Profesional',
    'training.programs.subtitle': 'Programas de certificación integrales diseñados para diferentes niveles de habilidad e industrias',
    'training.delivery.title': 'Entrega de Formación Flexible',
    'training.delivery.online': 'Formación en Línea',
    'training.delivery.onsite': 'Formación Presencial',
    'training.delivery.hybrid': 'Programas Híbridos',
    
    // Training Levels
    'training.l1.title': 'Fundamentos de IA e Ingeniería de Prompts',
    'training.l1.description': 'Domina los conceptos básicos de IA y técnicas de ingeniería de prompts para máxima productividad',
    'training.l1.topic1': 'Ingeniería de Prompts',
    'training.l1.topic2': 'Selección de Modelos IA',
    'training.l1.topic3': 'Seguridad y Ética',
    'training.l1.topic4': 'Aplicaciones Prácticas',
    'training.l1.audience1': 'Principiantes',
    'training.l1.audience2': 'Usuarios de Negocio',
    'training.l1.audience3': 'Líderes de Equipo',
    'training.l1.audience4': 'Roles No Técnicos',
    
    'training.l2.title': 'Automatización Agéntica e Inteligencia de Procesos',
    'training.l2.description': 'Construye agentes de IA autónomos que entienden, aprenden y optimizan procesos de negocio',
    'training.l2.topic1': 'Diseño de Agentes IA',
    'training.l2.topic2': 'Toma de Decisiones Autónoma',
    'training.l2.topic3': 'Inteligencia de Procesos',
    'training.l2.topic4': 'Orquestación de Agentes',
    'training.l2.audience1': 'Gestores de Procesos',
    'training.l2.audience2': 'Profesionales IT',
    'training.l2.audience3': 'Equipos Operacionales',
    'training.l2.audience4': 'Especialistas en Automatización',
    
    'training.l3.title': 'Especialista en Integración de IA',
    'training.l3.description': 'Implementación avanzada de IA e integración de modelos personalizados',
    'training.l3.topic1': 'Modelos IA Personalizados',
    'training.l3.topic2': 'Integración API',
    'training.l3.topic3': 'Pipeline de Datos',
    'training.l3.topic4': 'Entrenamiento de Modelos',
    'training.l3.audience1': 'Ingenieros de Software',
    'training.l3.audience2': 'Científicos de Datos',
    'training.l3.audience3': 'Desarrolladores IA',
    'training.l3.audience4': 'Líderes Técnicos',
    
    'training.l4.title': 'Arquitectura IA Cloud y Seguridad',
    'training.l4.description': 'Diseño de infraestructura IA empresarial con marcos de seguridad avanzados',
    'training.l4.topic1': 'Infraestructura IA',
    'training.l4.topic2': 'Seguridad Cloud',
    'training.l4.topic3': 'Gobernanza IA',
    'training.l4.topic4': 'Cumplimiento y Riesgos',
    'training.l4.audience1': 'Arquitectos de Soluciones',
    'training.l4.audience2': 'Ingenieros de Seguridad',
    'training.l4.audience3': 'Equipos DevOps',
    'training.l4.audience4': 'Directores IT',
    
    'training.l5.title': 'Estrategia IA y Liderazgo Ejecutivo',
    'training.l5.description': 'Estrategia IA de nivel C, gobernanza y liderazgo de transformación organizacional',
    'training.l5.topic1': 'Estrategia IA',
    'training.l5.topic2': 'Liderazgo Digital',
    'training.l5.topic3': 'Gestión del Cambio',
    'training.l5.topic4': 'Ética y Gobernanza IA',
    'training.l5.audience1': 'Ejecutivos C-Level',
    'training.l5.audience2': 'Consultores Estratégicos',
    'training.l5.audience3': 'Líderes de Innovación',
    'training.l5.audience4': 'Miembros del Consejo',
    
    // Training Delivery Methods
    'training.online.title': 'Formación en Línea',
    'training.online.description': 'Sesiones virtuales interactivas con colaboración en tiempo real',
    'training.online.feature1': 'Instructor en Vivo',
    'training.online.feature2': 'Laboratorios Interactivos',
    'training.online.feature3': 'Acceso a Grabaciones',
    'training.online.feature4': 'Materiales Digitales',
    
    'training.onsite.title': 'Formación Presencial',
    'training.onsite.description': 'Programas personalizados entregados en su ubicación',
    'training.onsite.feature1': 'Contenido Personalizado',
    'training.onsite.feature2': 'Construcción de Equipos',
    'training.onsite.feature3': 'Configuración de Equipos',
    'training.onsite.feature4': 'Soporte de Seguimiento',
    
    'training.hybrid.title': 'Programas Híbridos',
    'training.hybrid.description': 'Mezcla de formación en línea y presencial para flexibilidad óptima',
    'training.hybrid.feature1': 'Horario Flexible',
    'training.hybrid.feature2': 'Lo Mejor de Ambos',
    'training.hybrid.feature3': 'Costo Efectivo',
    'training.hybrid.feature4': 'Alcance Global',
    
    // Training UI Elements
    'training.keytopics': 'Temas Clave',
    'training.targetaudience': 'Audiencia Objetivo',
    'training.free': 'Gratis',
    'training.professional': 'Profesional',
    'training.duration.hours': 'horas',
    'training.free.message.title': '¡Oportunidad de Formación Gratuita!',
    'training.free.message.description': 'Comienza a aprender gratis y obtén tu certificado reconocido globalmente al completar. ¡Sin costo, solo valor!',
    'training.free.button': 'Comenzar Formación Gratuita Ahora',
    'training.professional.title': 'Formación Profesional',
    'training.professional.description': 'Programas de formación personalizados con orientación experta y proyectos prácticos.',
    'training.book.button': 'Reservar Esta Formación Ahora',
    'training.certification.title': 'Beneficios de Certificación Profesional',
    'training.certification.description': 'Nuestras certificaciones reconocidas globalmente validan tu experiencia y avanzan tu carrera en transformación digital.',
    'training.certification.advancement': 'Avance Profesional',
    'training.certification.salary': 'Aumento Salarial Promedio',
    'training.certification.placement': 'Tasa de Colocación Laboral',
    
    // Footer
    'footer.company': 'Empresa',
    'footer.services': 'Servicios',
    'footer.contact': 'Contacto',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.description': 'Liderando la revolución de inteligencia autónoma con sistemas de IA cuántica seguros, redes de procesamiento neuronal y soluciones de infraestructura de próxima generación.',
    'footer.about': 'Acerca de Nosotros',
    'footer.neural.networks': 'Redes Neuronales',
    'footer.quantum.security': 'Seguridad Cuántica',
    'footer.edge.computing': 'Computación en el Borde',
    'footer.ai.orchestration': 'Orquestación de IA',
    'footer.neural.consulting': 'Consultoría Neuronal',
    'footer.architecture': 'Arquitectura',
    'footer.neural.research': 'Investigación Neuronal',
    'footer.copyright': '© 2024 QuantumBox AI. Todos los derechos reservados. Pioneros en inteligencia autónoma e infraestructura cuántica segura.',
    
    // Common
    'common.learnMore': 'Saber Más',
    'common.getStarted': 'Comenzar',
    'common.contactUs': 'Contáctanos',
    'common.required': 'requerido',
  },
  pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.about': 'Sobre',
    'nav.services': 'Serviços',
    'nav.assessment': 'Avaliação',
    'nav.contact': 'Contato',
    'nav.consultation': 'Consulta Gratuita',
    
    // Home Page
    'home.hero.title': 'Soluções de IA de Nova Geração para Transformação Digital',
    'home.hero.subtitle': 'Inteligência Autônoma • Segurança Quântica • Infraestrutura Avançada',
    'home.hero.cta1': 'Explorar Soluções de IA',
    'home.hero.cta2': 'Agendar Demo',
    'hero.cta.assessment': 'Fazer Avaliação',
    
    // About Page
    'about.title': 'Sobre a QuantumBox AI',
    'about.subtitle': 'Somos pioneiros na transformação digital impulsionada por IA, dedicados a revolucionar como as empresas operam, inovam e crescem na era digital.',
    'about.mission.title': 'Nossa Missão',
    'about.mission.description': 'Democratizar as tecnologias de inteligência artificial e automação, tornando soluções de ponta acessíveis para empresas de todos os tamanhos. Capacitamos organizações para aproveitar o poder transformador da IA para impulsionar inovação, eficiência e crescimento sustentável.',
    'about.vision.title': 'Nossa Visão',
    'about.vision.description': 'Ser o líder global em transformação digital impulsionada por IA, criando um futuro onde a automação inteligente melhore o potencial humano e impulsione o sucesso empresarial sem precedentes em todas as indústrias.',
    'about.values.title': 'Nossos Valores Fundamentais',
    'about.values.subtitle': 'Os princípios que guiam tudo o que fazemos',
    'about.values.innovation': 'Inovação',
    'about.values.innovation.desc': 'Empurramos os limites da tecnologia para criar soluções revolucionárias que transformam indústrias.',
    'about.values.excellence': 'Excelência',
    'about.values.excellence.desc': 'Estamos comprometidos em entregar serviços da mais alta qualidade e superar as expectativas do cliente.',
    'about.values.collaboration': 'Colaboração',
    'about.values.collaboration.desc': 'Trabalhamos em estreita colaboração com nossos clientes como parceiros, garantindo que seu sucesso impulsione nossa inovação.',
    'about.values.integrity': 'Integridade',
    'about.values.integrity.desc': 'Mantemos os mais altos padrões éticos e transparência em todos os nossos relacionamentos comerciais.',
    'about.story.title': 'Nossa História',
    'about.story.subtitle': 'Nascida de uma visão para democratizar a tecnologia de IA',
    'about.story.p1': 'Fundada no coração de Paris, a QuantumBox AI surgiu de uma crença simples, mas poderosa: que a inteligência artificial e a automação deveriam ser acessíveis a todas as empresas, independentemente do tamanho ou indústria. Nossos fundadores, veteranos da indústria de tecnologia com décadas de experiência combinada em IA, aprendizado de máquina e software empresarial, reconheceram a lacuna entre a pesquisa de IA de ponta e as aplicações comerciais práticas.',
    'about.story.p2': 'Hoje, estamos na vanguarda da revolução da IA, ajudando empresas em saúde, finanças, governo, logística e incontáveis outros setores a transformar suas operações através da automação inteligente. Nossa equipe de engenheiros de classe mundial, cientistas de dados e estrategistas de negócios trabalha incansavelmente para empurrar os limites do que é possível com a tecnologia de IA.',
    'about.story.p3': 'De modelos de linguagem grandes personalizados a chatbots avançados com embeddings RAG, de infraestrutura em nuvem a soluções de segurança, construímos nossa reputação entregando resultados que importam. Cada projeto que empreendemos é impulsionado por nosso compromisso com a excelência e nossa paixão pela inovação.',
    'about.cta.title': 'Pronto para Transformar seu Negócio?',
    'about.cta.subtitle': 'Junte-se às centenas de empresas que revolucionaram suas operações com a QuantumBox AI.',
    'about.cta.button': 'Comece sua Jornada',
    
    // Services
    'services.title': 'Nossos Serviços',
    'services.subtitle': 'Soluções abrangentes de IA e tecnologia projetadas para transformar seu negócio, otimizar operações e impulsionar crescimento sustentável na era digital.',
    'services.digital.title': 'Soluções de Transformação Digital',
    'services.digital.desc': 'Soluções abrangentes de ponta a ponta para modernizar seus processos de negócios e abraçar o futuro digital.',
    'services.ai.title': 'IA e Automação',
    'services.ai.desc': 'Soluções avançadas de inteligência artificial que automatizam fluxos de trabalho complexos e otimizam operações comerciais.',
    'services.workflow.title': 'Otimização de Fluxos de Trabalho',
    'services.workflow.desc': 'Otimize suas operações com ferramentas e estratégias inteligentes que melhoram a eficiência e produtividade.',
    'services.document.title': 'Automação de Documentos',
    'services.document.desc': 'Soluções inteligentes para processar documentos estruturados e não estruturados com precisão impulsionada por IA.',
    'services.model.title': 'Treinamento de Modelos Personalizados',
    'services.model.desc': 'Modelos de linguagem grandes privados e modelos de linguagem pequenos adaptados às suas necessidades comerciais específicas.',
    'services.chatbot.title': 'Chatbots Avançados',
    'services.chatbot.desc': 'IA conversacional de próxima geração com embeddings RAG para interação e suporte ao cliente superior.',
    'services.app.title': 'Desenvolvimento de Aplicações',
    'services.app.desc': 'Desenvolvimento de aplicações web e móveis personalizadas usando tecnologias e frameworks de ponta.',
    'services.consulting.title': 'Serviços de Consultoria',
    'services.consulting.desc': 'Orientação estratégica especializada para navegar sua jornada de transformação digital com confiança.',
    'services.cloud.title': 'Computação em Nuvem',
    'services.cloud.desc': 'Soluções em nuvem robustas e escaláveis que garantem operações comerciais seguras e eficientes.',
    'services.security.title': 'Soluções de Segurança',
    'services.security.desc': 'Medidas abrangentes de cibersegurança para proteger seus ativos digitais e dados comerciais sensíveis.',
    'services.server.title': 'Soluções de Servidor',
    'services.server.desc': 'Última tecnologia de servidor incluindo soluções Cisco premium para infraestrutura comercial otimizada.',
    'services.research.title': 'Pesquisa e Inovação',
    'services.research.desc': 'Pesquisa e desenvolvimento de ponta em saúde, mineração, finanças e indústrias especializadas.',
    'services.training.title': 'Treinamento e Certificações',
    'services.training.description': 'Programas abrangentes de treinamento em transformação digital do nível L1 ao avançado, disponíveis online ou presencialmente.',
    'services.impact.title': 'Impacto Real',
    'services.impact.subtitle': 'Veja como nossas soluções estão transformando indústrias e entregando resultados mensuráveis para nossos clientes.',
    'services.cta.title': 'Pronto para Começar?',
    'services.cta.subtitle': 'Vamos discutir como nossos serviços podem transformar seu negócio e impulsionar crescimento sem precedentes.',
    'services.cta.consultation': 'Agendar Consulta',
    'services.cta.learn': 'Saiba Mais Sobre Nós',
    
    // Contact
    'contact.title': 'Entre em Contato',
    'contact.subtitle': 'Pronto para transformar seu negócio com IA? Vamos discutir como nossas soluções inovadoras podem impulsionar sua transformação digital e desbloquear novas oportunidades.',
    'contact.form.title': 'Envie-nos uma Mensagem',
    'contact.form.description': 'Preencha o formulário abaixo e responderemos em 24 horas.',
    'contact.form.firstName': 'Nome',
    'contact.form.lastName': 'Sobrenome',
    'contact.form.email': 'Endereço de Email',
    'contact.form.company': 'Empresa',
    'contact.form.phone': 'Número de Telefone',
    'contact.form.message': 'Mensagem',
    'contact.form.placeholder': 'Conte-nos sobre seu projeto, objetivos e como podemos ajudá-lo...',
    'contact.form.submit': 'Enviar Mensagem',
    'contact.form.sending': 'Enviando...',
    'contact.info.title': 'Informações de Contato',
    'contact.info.subtitle': 'Múltiplas formas de entrar em contato com nossa equipe',
    'contact.info.email': 'Email',
    'contact.info.email.desc': 'Envie-nos uma mensagem a qualquer momento',
    'contact.info.location': 'Localização',
    'contact.info.location.value': 'Paris, França',
    'contact.info.location.desc': 'Sede europeia',
    'contact.info.response': 'Tempo de Resposta',
    'contact.info.response.value': '< 24 horas',
    'contact.info.response.desc': 'Respondemos rapidamente',
    'contact.why.title': 'Por Que se Associar a Nós?',
    'contact.why.expert': 'Consulta Especializada',
    'contact.why.expert.desc': 'Avaliação inicial gratuita e planejamento estratégico',
    'contact.why.rapid': 'Resposta Rápida',
    'contact.why.rapid.desc': 'Tempo de resposta rápido em propostas e soluções',
    'contact.why.proven': 'Resultados Comprovados',
    'contact.why.proven.desc': 'Histórico de implementações bem-sucedidas',
    'contact.why.support': 'Suporte Contínuo',
    'contact.why.support.desc': 'Otimização e manutenção contínuas',
    'contact.faq.title': 'Perguntas Frequentes',
    'contact.faq.subtitle': 'Respostas rápidas para perguntas comuns sobre nossos serviços',
    'contact.faq.start': 'Quão rapidamente podemos começar?',
    'contact.faq.start.answer': 'A maioria dos projetos pode começar em 1-2 semanas após consulta inicial e definição de escopo.',
    'contact.faq.small': 'Vocês trabalham com pequenas empresas?',
    'contact.faq.small.answer': 'Absolutamente! Fornecemos soluções escaláveis para empresas de todos os tamanhos, desde startups até grandes corporações.',
    'contact.faq.industries': 'Quais indústrias vocês atendem?',
    'contact.faq.industries.answer': 'Trabalhamos em todas as indústrias incluindo saúde, finanças, governo, logística e mais.',
    'contact.faq.ongoing': 'Vocês fornecem suporte contínuo?',
    'contact.faq.ongoing.answer': 'Sim, oferecemos serviços abrangentes de manutenção, monitoramento e otimização para todas as nossas soluções.',
    
    // Home/Hero
    'hero.badge': 'Infraestrutura de IA Nova Geração',
    'hero.title.quantum': 'Quantum',
    'hero.title.box': 'Box',
    'hero.title.ai': 'IA',
    'hero.tagline.1': 'Inteligência Autônoma',
    'hero.tagline.2': 'Infraestrutura Segura',
    'hero.tagline.3': 'Escalabilidade Dinâmica',
    'hero.description': 'Construindo a arquitetura empresarial de amanhã com agentes de IA, redes de processamento neural e infraestrutura quântica segura que se adapta, aprende e evolui.',
    'hero.cta.initialize': 'Inicializar Sistema',
    'hero.cta.automation': 'Avaliação Gratuita de Automação',
    'hero.cta.explore': 'Explorar Arquitetura',
    'hero.stats.neural': 'Precisão Neural',
    'hero.stats.processing': 'Processamento Mais Rápido',
    'hero.stats.security': 'Segurança Quântica',
    'hero.stats.operation': 'Operação Autônoma',
    
    // Tech Services Overview
    'tech.title': 'Arquitetura de IA Avançada',
    'tech.description': 'Infraestrutura de IA de nível empresarial projetada para o futuro. Nossos sistemas autônomos criam soluções inteligentes, seguras e escaláveis que evoluem com seu negócio.',
    'tech.agents.title': 'Agentes de IA Autônomos',
    'tech.agents.desc': 'Sistemas neurais auto-aprendizes que operam independentemente, tomando decisões inteligentes e otimizando processos em tempo real.',
    'tech.security.title': 'Infraestrutura Quântica Segura',
    'tech.security.desc': 'Arquitetura de segurança de próxima geração com criptografia quântica, detecção de ameaças e sistemas de defesa autônomos.',
    'tech.network.title': 'Arquitetura de Rede Neural',
    'tech.network.desc': 'Redes de processamento de IA distribuídas que escalam dinamicamente e se adaptam às demandas de carga de trabalho.',
    'tech.data.title': 'Malha de Dados Inteligente',
    'tech.data.desc': 'Infraestrutura de dados auto-organizativa com otimização impulsionada por IA e análise em tempo real.',
    'tech.zerotrust.title': 'Segurança Zero-Trust',
    'tech.zerotrust.desc': 'Ecossistema de segurança abrangente com verificação contínua e mitigação de ameaças.',
    'tech.edge.title': 'Processamento de IA na Borda',
    'tech.edge.desc': 'Inteligência distribuída na borda com tomada de decisão e processamento de baixa latência.',
    'tech.analytics.title': 'Motor de Análise Preditiva',
    'tech.analytics.desc': 'Sistemas de previsão avançados que antecipam tendências e otimizam resultados comerciais.',
    'tech.orchestration.title': 'Orquestração em Tempo Real',
    'tech.orchestration.desc': 'Gestão dinâmica de fluxos de trabalho com alocação inteligente de recursos e otimização.',
    'tech.performance.title': 'Desempenho da Infraestrutura',
    'tech.performance.uptime': 'Tempo de Atividade do Sistema',
    'tech.performance.response': 'Tempo de Resposta',
    'tech.performance.operations': 'Operações/Dia',
    'tech.performance.automated': 'Automatizado',
    'tech.cta': 'Explorar Arquitetura Completa',
    
    // Training Programs
    'training.programs.title': 'Programas de Treinamento Profissional',
    'training.programs.subtitle': 'Programas de certificação abrangentes projetados para diferentes níveis de habilidade e indústrias',
    'training.delivery.title': 'Entrega de Treinamento Flexível',
    'training.delivery.online': 'Treinamento Online',
    'training.delivery.onsite': 'Treinamento Presencial',
    'training.delivery.hybrid': 'Programas Híbridos',
    
    // Training Levels
    'training.l1.title': 'Fundamentos de IA e Engenharia de Prompts',
    'training.l1.description': 'Domine os conceitos básicos de IA e técnicas de engenharia de prompts para máxima produtividade',
    'training.l1.topic1': 'Engenharia de Prompts',
    'training.l1.topic2': 'Seleção de Modelos IA',
    'training.l1.topic3': 'Segurança e Ética',
    'training.l1.topic4': 'Aplicações Práticas',
    'training.l1.audience1': 'Iniciantes',
    'training.l1.audience2': 'Usuários de Negócio',
    'training.l1.audience3': 'Líderes de Equipe',
    'training.l1.audience4': 'Funções Não Técnicas',
    
    'training.l2.title': 'Automação Agêntica e Inteligência de Processos',
    'training.l2.description': 'Construa agentes de IA autônomos que entendem, aprendem e otimizam processos de negócio',
    'training.l2.topic1': 'Design de Agentes IA',
    'training.l2.topic2': 'Tomada de Decisão Autônoma',
    'training.l2.topic3': 'Inteligência de Processos',
    'training.l2.topic4': 'Orquestração de Agentes',
    'training.l2.audience1': 'Gestores de Processos',
    'training.l2.audience2': 'Profissionais de TI',
    'training.l2.audience3': 'Equipes Operacionais',
    'training.l2.audience4': 'Especialistas em Automação',
    
    'training.l3.title': 'Especialista em Integração de IA',
    'training.l3.description': 'Implementação avançada de IA e integração de modelos personalizados',
    'training.l3.topic1': 'Modelos IA Personalizados',
    'training.l3.topic2': 'Integração de API',
    'training.l3.topic3': 'Pipeline de Dados',
    'training.l3.topic4': 'Treinamento de Modelos',
    'training.l3.audience1': 'Engenheiros de Software',
    'training.l3.audience2': 'Cientistas de Dados',
    'training.l3.audience3': 'Desenvolvedores IA',
    'training.l3.audience4': 'Líderes Técnicos',
    
    'training.l4.title': 'Arquitetura IA Cloud e Segurança',
    'training.l4.description': 'Design de infraestrutura IA empresarial com frameworks de segurança avançados',
    'training.l4.topic1': 'Infraestrutura IA',
    'training.l4.topic2': 'Segurança Cloud',
    'training.l4.topic3': 'Governança IA',
    'training.l4.topic4': 'Conformidade e Riscos',
    'training.l4.audience1': 'Arquitetos de Soluções',
    'training.l4.audience2': 'Engenheiros de Segurança',
    'training.l4.audience3': 'Equipes DevOps',
    'training.l4.audience4': 'Diretores de TI',
    
    'training.l5.title': 'Estratégia IA e Liderança Executiva',
    'training.l5.description': 'Estratégia IA de nível C, governança e liderança de transformação organizacional',
    'training.l5.topic1': 'Estratégia IA',
    'training.l5.topic2': 'Liderança Digital',
    'training.l5.topic3': 'Gestão de Mudanças',
    'training.l5.topic4': 'Ética e Governança IA',
    'training.l5.audience1': 'Executivos C-Level',
    'training.l5.audience2': 'Consultores Estratégicos',
    'training.l5.audience3': 'Líderes de Inovação',
    'training.l5.audience4': 'Membros do Conselho',
    
    // Training Delivery Methods
    'training.online.title': 'Treinamento Online',
    'training.online.description': 'Sessões virtuais interativas com colaboração em tempo real',
    'training.online.feature1': 'Instrutor ao Vivo',
    'training.online.feature2': 'Laboratórios Interativos',
    'training.online.feature3': 'Acesso a Gravações',
    'training.online.feature4': 'Materiais Digitais',
    
    'training.onsite.title': 'Treinamento Presencial',
    'training.onsite.description': 'Programas personalizados entregues em sua localização',
    'training.onsite.feature1': 'Conteúdo Personalizado',
    'training.onsite.feature2': 'Construção de Equipes',
    'training.onsite.feature3': 'Configuração de Equipamentos',
    'training.onsite.feature4': 'Suporte de Acompanhamento',
    
    'training.hybrid.title': 'Programas Híbridos',
    'training.hybrid.description': 'Mistura de treinamento online e presencial para flexibilidade ideal',
    'training.hybrid.feature1': 'Horário Flexível',
    'training.hybrid.feature2': 'O Melhor dos Dois',
    'training.hybrid.feature3': 'Custo Efetivo',
    'training.hybrid.feature4': 'Alcance Global',
    
    // Training UI Elements
    'training.keytopics': 'Tópicos Principais',
    'training.targetaudience': 'Público Alvo',
    'training.free': 'Grátis',
    'training.professional': 'Profissional',
    'training.duration.hours': 'horas',
    'training.free.message.title': 'Oportunidade de Treinamento Gratuito!',
    'training.free.message.description': 'Comece a aprender gratuitamente e obtenha seu certificado reconhecido globalmente ao concluir. Sem custo, apenas valor!',
    'training.free.button': 'Começar Treinamento Gratuito Agora',
    'training.professional.title': 'Treinamento Profissional',
    'training.professional.description': 'Programas de treinamento personalizados com orientação especializada e projetos práticos.',
    'training.book.button': 'Reservar Este Treinamento Agora',
    'training.certification.title': 'Benefícios da Certificação Profissional',
    'training.certification.description': 'Nossas certificações reconhecidas globalmente validam sua expertise e avançam sua carreira em transformação digital.',
    'training.certification.advancement': 'Avanço na Carreira',
    'training.certification.salary': 'Aumento Salarial Médio',
    'training.certification.placement': 'Taxa de Colocação no Trabalho',
    
    // Footer
    'footer.company': 'Empresa',
    'footer.services': 'Serviços',
    'footer.contact': 'Contato',
    'footer.rights': 'Todos os direitos reservados.',
    'footer.description': 'Liderando a revolução da inteligência autônoma com sistemas de IA quântica seguros, redes de processamento neural e soluções de infraestrutura de próxima geração.',
    'footer.about': 'Sobre Nós',
    'footer.neural.networks': 'Redes Neurais',
    'footer.quantum.security': 'Segurança Quântica',
    'footer.edge.computing': 'Computação na Borda',
    'footer.ai.orchestration': 'Orquestração de IA',
    'footer.neural.consulting': 'Consultoria Neural',
    'footer.architecture': 'Arquitetura',
    'footer.neural.research': 'Pesquisa Neural',
    'footer.copyright': '© 2024 QuantumBox AI. Todos os direitos reservados. Pioneiros em inteligência autônoma e infraestrutura quântica segura.',
    
    // Common
    'common.learnMore': 'Saber Mais',
    'common.getStarted': 'Começar',
    'common.contactUs': 'Entre em Contato',
    'common.required': 'obrigatório',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['en', 'fr', 'es', 'pt'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};